import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Paper,
  ListItemIcon,
  Tooltip,
  TablePagination,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  NavigateNext as NavigateNextIcon,
  Business as BusinessIcon,
  AddCircleOutlineOutlined,
  Edit as EditIcon,
  Save,
  Cancel,
  DescriptionOutlined,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { tokens } from "../../../theme";
import { getCrmAccent } from "../../../constants/crmAccents";
import apiService from "../../../service/Api";
import { BookingResponse } from "../../bookings/types/view-booking";
import UploadDocuments from "../../bookings/components/UploadDocuments";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import formatDate from "../../../utils/FormatDate";

interface CompanyContactDetails {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address_one?: string;
  address_two?: string;
  suburb?: string;
  city?: string;
  country?: string;
  website?: string;
  notes?: string;
  facebook_profile?: string;
  instagram_profile?: string;
  twitter_profile?: string;
  linkedin_profile?: string;
  contact_image?: string;
  company?: { id: number; name: string; logo?: string };
  bookings?: { past: number; upcoming: number };
  payments?: { id: number; transaction: string; amount: number }[];
  created_by?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  last_updated_by?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

interface DocumentItem {
  id: number;
  name: string;
  description?: string;
  document: string;
}

interface PaginatedDocumentResponse {
  results: DocumentItem[];
}

interface PaginatedResponse<T> {
  results: T[];
}

interface UploadedDocumentResponse {
  id: number;
  name: string;
  description?: string;
  document: string;
}

const CompanyContactView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  // Companies are themed indigo across the CRM; green marks upcoming activity.
  const accent = getCrmAccent("indigo");
  const accentAlt = getCrmAccent("green");
  const [contactData, setContactData] = useState<CompanyContactDetails | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"upcoming" | "past">("upcoming");
  const [page, setPage] = useState(0);
  const rowsPerPage = 5; // change this as needed

  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteContent, setNoteContent] = useState<string>("");
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [filteredBookings, setFilteredBookings] = useState<BookingResponse[]>(
    []
  );
  const [dialogDocumentOpen, setDialogDocumentOpen] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentDescription, setDocumentDescription] = useState("");
  const [documentCategory, setDocumentCategory] = useState("");
  const [documents, setDocuments] = useState<DocumentItem[]>([]);

  const handleAddDocument = () => {
    setDocumentName("");
    setDocumentDescription("");
    setDocumentCategory("");
    setDocumentFile(null);
    setDialogDocumentOpen(true);
  };

  const handleSaveDocument = async () => {
    if (!documentFile || !documentName || !id || !documentDescription) {
      showErrorToast(
        "Missing document, name, description, or contact ID. Please provide all details."
      );
      return;
    }
    try {
      const formData = new FormData();
      formData.append("contact", id!);
      formData.append("name", documentName);
      formData.append("category", documentCategory);
      formData.append("description", documentDescription);
      formData.append("document", documentFile);

      const response = await apiService.post<UploadedDocumentResponse>(
        "/api/contacts/contact-documents/",
        formData
      );

      const uploadedDoc = response.data;
      setDocuments((prev) => [
        ...prev,
        {
          id: uploadedDoc.id,
          name: uploadedDoc.name,
          description: uploadedDoc.description,
          document: uploadedDoc.document,
        },
      ]);
      showSuccessToast("Document uploaded successfully!");
      setDialogDocumentOpen(false);
      setDocumentName("");
      setDocumentFile(null);
    } catch (error) {
      console.error("Failed to upload document:", error);
      showErrorToast("Failed to upload document. Please try again.");
    }
  };

  const handleSaveNote = async () => {
    if (!id) return;
    try {
      await apiService.patch(`/api/contacts/contact/${id}/`, {
        notes: noteContent,
      });
      setContactData((prev) => (prev ? { ...prev, notes: noteContent } : prev));
      showSuccessToast("Note saved successfully!");
      setIsAddingNote(false);
      setIsEditingNote(false);
    } catch (error) {
      console.error("Failed to save note:", error);
      showErrorToast("Failed to save note. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setIsEditingNote(false);
    setIsAddingNote(false);
    if (contactData?.notes) {
      setNoteContent(contactData.notes);
    } else {
      setNoteContent("");
    }
  };

  const handleAddClick = () => {
    setIsAddingNote(true);
    setIsEditingNote(true);
    setNoteContent("");
  };

  const handleEditClick = () => {
    setIsEditingNote(true);
    setIsAddingNote(true);
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await apiService.get<
          PaginatedResponse<BookingResponse>
        >("/api/bookings/booking/");
        const bookings = response.data.results;
        const matched = bookings.filter(
          (booking: BookingResponse) =>
            Number(booking.contact?.id) === Number(id)
        );
        setFilteredBookings(
          matched.filter((booking) =>
            activeTab === "upcoming"
              ? new Date(booking.services?.[0]?.date_of_service || "") >=
                new Date()
              : new Date(booking.services?.[0]?.date_of_service || "") <
                new Date()
          )
        );
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Failed to load bookings.";
        console.error(errorMessage);
      }
    };

    const fetchContactAndDocuments = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch contact
        const contactResponse = await apiService.get<CompanyContactDetails>(
          `/api/contacts/contact/${id}/`
        );
        setContactData(contactResponse.data);
        setNoteContent(contactResponse.data?.notes || "");

        // Fetch documents
        const documentsResponse =
          await apiService.get<PaginatedDocumentResponse>(
            `/api/contacts/contact-documents/?contact=${id}`
          );
        setDocuments(documentsResponse.data.results || []);
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          "Failed to load company contact details or documents.";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchContactAndDocuments();
      fetchBookings();
    }
  }, [id, activeTab]);
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <Typography variant="h6">Loading company contact details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!contactData) {
    return null;
  }

  const totalAmount = (contactData.payments ?? []).reduce(
    (sum, payment) => sum + payment.amount,
    0
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{
              border: `2px solid ${colors.primary[100]}`,
              color: colors.primary[100],
            }}
            onClick={() => {
              navigate(`/contacts/company/${id}/edit`);
            }}
          >
            Edit
          </Button>
        </Box>
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              style={{ height: "100%" }}
            >
              <Card
                sx={{
                  display: "flex",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  height: "100%",
                  borderTop: `4px solid ${accent.solid}`,
                  borderTopWidth: 4,
                  borderRadius: 3,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Avatar
                      src={contactData.contact_image}
                      sx={{
                        width: 120,
                        height: 120,
                        mx: "auto",
                        mb: 2,
                        bgcolor: accent.bg,
                        color: accent.fg,
                        border: `3px solid ${accent.border}`,
                        fontSize: "2.6rem",
                        fontWeight: 700,
                      }}
                    >
                      {contactData.name?.[0] ?? ""}
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {contactData.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {contactData.email}
                    </Typography>

                    {/* Bookings Stats */}
                    <Box sx={{ my: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Bookings
                      </Typography>
                      <Box
                        sx={{ display: "flex", justifyContent: "space-around" }}
                      >
                        <Box
                          sx={{
                            textAlign: "center",
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: accent.bg,
                            minWidth: 86,
                          }}
                        >
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{ color: accent.fg }}
                          >
                            {contactData.bookings?.past ?? 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Past
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            textAlign: "center",
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: accentAlt.bg,
                            minWidth: 86,
                          }}
                        >
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{ color: accentAlt.fg }}
                          >
                            {contactData.bookings?.upcoming ?? 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Upcoming
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        mb: 2,
                        bgcolor: `${colors.primary[400]} !important`,
                        color: colors.primary[900],
                      }}
                    >
                      Send Message
                    </Button>

                    {/* Contact Actions */}
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 2 }}
                    >
                      <IconButton>
                        <PhoneIcon />
                      </IconButton>
                      <IconButton>
                        <LanguageIcon />
                      </IconButton>
                      <IconButton>
                        <EmailIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Box>

                {/* Right Side - Contact Details */}
                <Box sx={{ flex: 1 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Company
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {contactData.company?.name || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {contactData.phone || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Address
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {contactData.address_one || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          City
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {contactData.city || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Country
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {contactData.country || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Website
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {contactData.website || "N/A"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Box>
              </Card>
            </motion.div>
          </Grid>
          {/* Notes Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Notes
                    </Typography>
                    {!isAddingNote && !isEditingNote && (
                      <IconButton
                        size="small"
                        onClick={noteContent ? handleEditClick : handleAddClick}
                      >
                        {noteContent ? (
                          <EditIcon />
                        ) : (
                          <AddCircleOutlineOutlined />
                        )}
                      </IconButton>
                    )}
                  </Box>

                  {/* Display/Edit Note UI */}
                  {isAddingNote || isEditingNote ? (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        multiline
                        rows={4}
                        fullWidth
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        placeholder="Type your note..."
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton color="primary" onClick={handleSaveNote}>
                          <Save />
                        </IconButton>
                        <IconButton
                          color="secondary"
                          onClick={handleCancelEdit}
                        >
                          <Cancel />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mb: 2,
                        border: "1px solid #eee",
                        borderRadius: 1,
                        p: 1,
                        minHeight: "100px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {noteContent ||
                          "No notes yet. Click the '+' or 'Edit' button to add one."}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          {/* Files/Documents Section */}
          <Grid size={3}>
            <Paper
              sx={{
                p: 2,
                height: "100%",
                boxShadow: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: 16, fontWeight: "bold" }}
                >
                  Documents
                </Typography>
                <IconButton
                  onClick={handleAddDocument}
                  sx={{
                    bgcolor: theme.palette.grey[100],
                    color: theme.palette.text.secondary,
                    "&:hover": { bgcolor: theme.palette.grey[200] },
                  }}
                >
                  <AddCircleOutlineOutlined />
                </IconButton>
              </Box>

              {documents.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                    mt: 2,
                  }}
                >
                  No documents found
                </Typography>
              ) : (
                <List disablePadding>
                  {documents.map((doc) => (
                    <ListItem
                      key={doc.id}
                      sx={{
                        mb: 1,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        "&:hover": {
                          bgcolor: theme.palette.action.hover,
                          cursor: "pointer",
                        },
                        p: 1.5,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <DescriptionOutlined
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 14,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {doc.name}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
          {/* Bookings Table */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.6 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant={
                          activeTab === "upcoming" ? "contained" : "text"
                        }
                        onClick={() => setActiveTab("upcoming")}
                        size="small"
                      >
                        Upcoming Booking
                      </Button>
                      <Button
                        variant={activeTab === "past" ? "contained" : "text"}
                        onClick={() => setActiveTab("past")}
                        size="small"
                      >
                        Past Booking
                      </Button>
                    </Box>
                    <IconButton size="small">
                      <AddCircleOutlineOutlined />
                    </IconButton>
                  </Box>

                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                          <TableCell>Name</TableCell>
                          <TableCell>Service</TableCell>
                          <TableCell>Package</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Array.isArray(filteredBookings) &&
                          filteredBookings
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((booking, index) => (
                              <TableRow
                                key={booking.id}
                                sx={{
                                  backgroundColor:
                                    index % 2 === 0
                                      ? "white"
                                      : colors.primary[800],
                                  cursor: "pointer", // makes it obvious the row is clickable
                                  "&:hover": {
                                    backgroundColor: colors.grey[500], // hover color
                                  },
                                }}
                               
                              >
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Avatar
                                      src={
                                        booking?.deceased?.[0]?.person_image ||
                                        ""
                                      }
                                      sx={{ width: 32, height: 32 }}
                                    >
                                      {(
                                        booking?.deceased?.[0]
                                          ?.first_name?.[0] || ""
                                      ).toUpperCase()}
                                    </Avatar>

                                    <Typography variant="body2">
                                      {booking?.deceased?.[0]?.first_name &&
                                      booking?.deceased?.[0]?.surname
                                        ? `${booking.deceased[0].first_name} ${booking.deceased[0].surname}`
                                        : booking?.title ?? ""}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{booking.service}</TableCell>
                                <TableCell>
                                  {booking.package_item?.name}
                                </TableCell>
                                <TableCell>
                                  {formatDate(
                                    booking.services?.[0]?.date_of_service
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination - Only show if bookings > 5 */}
                  {filteredBookings.length > rowsPerPage && (
                    <TablePagination
                      component="div"
                      count={filteredBookings.length}
                      page={page}
                      onPageChange={(event, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[rowsPerPage]}
                    />
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Companies Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Company
                  </Typography>
                  <List dense>
                    {contactData.company ? (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar
                            src={contactData.company.logo}
                            sx={{ width: 32, height: 32 }}
                          >
                            <BusinessIcon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={contactData.company.name} />
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/companies/${contactData.company?.id}`)
                          }
                        >
                          <NavigateNextIcon />
                        </IconButton>
                      </ListItem>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No company associated
                      </Typography>
                    )}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          {/* Payments Section */}
          <Grid size={{ xs: 12, md: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              style={{ height: "100%" }}
            >
              <Card sx={{ boxShadow: 0, height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Payments
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Transaction
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Amount
                      </Typography>
                    </Box>
                    {(contactData.payments ?? []).map((payment) => (
                      <Box
                        key={payment.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {payment.transaction}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          ${payment.amount}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        Total amount
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${totalAmount}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
      {/* Created By Section */}

      {(contactData.created_by || contactData.last_updated_by) && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            width: "100%",
            padding: "16px 24px",
            marginTop: "20px",
            backgroundColor: colors.primary[900],
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            borderRadius: "8px",
          }}
        >
          {/* 👈 Created By - Left side */}
          {contactData.created_by && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color={colors.neutral[100]}
                sx={{ fontWeight: "medium" }}
              >
                Created By
              </Typography>

              <Tooltip
                title={contactData.created_by?.email || ""}
                placement="top"
              >
                <Typography
                  variant="body2"
                  color={colors.primary[400]}
                  sx={{ fontWeight: "bold" }}
                >
                  {contactData?.created_by?.first_name}{" "}
                  {contactData?.created_by?.last_name}
                </Typography>
              </Tooltip>
            </Box>
          )}
          {/* 👉 Updated By - Right side */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              color={colors.neutral[100]}
              sx={{ fontWeight: "medium" }}
            >
              Last Updated By
            </Typography>

            {contactData.last_updated_by &&
            typeof contactData.last_updated_by !== "string" ? (
              <Tooltip
                title={contactData.last_updated_by?.email || ""}
                placement="top"
              >
                <Typography
                  variant="body2"
                  color={colors.primary[400]}
                  sx={{ fontWeight: "bold" }}
                >
                  {contactData.last_updated_by?.first_name}{" "}
                  {contactData.last_updated_by?.last_name}
                </Typography>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                color={colors.primary[400]}
                sx={{ fontWeight: "bold" }}
              >
                —
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <UploadDocuments
        open={dialogDocumentOpen}
        loading={isLoading}
        setDialogDocumentOpen={setDialogDocumentOpen}
        setDocumentCategory={setDocumentCategory}
        documentCategory={documentCategory}
        documentName={documentName}
        documentDescription={documentDescription}
        setDocumentDescription={setDocumentDescription}
        setDocumentName={setDocumentName}
        setDocumentFile={setDocumentFile}
        documentFile={documentFile}
        handleSaveDocument={handleSaveDocument}
      />
    </motion.div>
  );
};

export default CompanyContactView;
